import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Radio, Loader2, Tv, Trophy } from "lucide-react";

export default function Sports() {
  const [catalog, setCatalog] = useState([]);
  const [settings, setSettings] = useState({ enabled: false, league_key: "fifa.world", every: 3 });
  const [tvs, setTvs] = useState([]);
  const [live, setLive] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadLive = useCallback((key) => {
    api.get("/sports/live", { params: key ? { league_key: key } : {} })
      .then((r) => {
        setLive(r.data);
        if (r.data?.auto_closed) {
          setSettings((s) => ({ ...s, enabled: false }));
          toast.info("Event ended — live sports turned off automatically");
        }
      }).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([api.get("/sports/catalog"), api.get("/sports/settings"), api.get("/tvs")])
      .then(([c, s, t]) => {
        setCatalog(c.data);
        setSettings({ enabled: s.data.enabled, league_key: s.data.league_key, every: s.data.every || 3 });
        setTvs(t.data);
        loadLive(s.data.league_key);
      });
  }, [loadLive]);

  // refresh scores every 12s
  useEffect(() => {
    const i = setInterval(() => loadLive(settings.league_key), 12000);
    return () => clearInterval(i);
  }, [settings.league_key, loadLive]);

  const save = async (next) => {
    const body = { ...settings, ...next };
    setSettings(body);
    setSaving(true);
    try {
      await api.post("/sports/settings", body);
      toast.success(body.enabled ? "Live sports is broadcasting" : "Live sports turned off");
    } catch { toast.error("Could not save"); }
    setSaving(false);
  };

  const setLeague = (key) => { save({ league_key: key }); loadLive(key); };

  const previewGoal = async () => {
    try {
      await api.post("/sports/test-goal");
      toast.success("🎉 Goal celebration sent to all live TVs!");
    } catch { toast.error("Could not trigger"); }
  };

  const toggleTv = async (tv) => {
    const sports_off = !tv.sports_off ? true : false;
    setTvs((prev) => prev.map((t) => (t.id === tv.id ? { ...t, sports_off } : t)));
    try { await api.post(`/tvs/${tv.id}/settings`, { sports_off }); }
    catch { toast.error("Could not update TV"); }
  };

  const active = catalog.find((c) => c.key === settings.league_key);

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-5xl">
      <div className="mb-5">
        <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight flex items-center gap-2">
          <Trophy className="w-6 h-6 text-primary" /> Live Sports
        </h1>
        <p className="text-sm text-muted-foreground mt-0.5">Broadcast live scores between your slides — with a goal celebration on every score.</p>
      </div>

      {/* Master control */}
      <div className="bg-card border border-border rounded-xl p-4 md:p-5 mb-5">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${settings.enabled ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`}>
              <Radio className={`w-5 h-5 ${settings.enabled ? "live-dot" : ""}`} />
            </div>
            <div className="min-w-0">
              <p className="font-semibold">Broadcast on all TVs</p>
              <p className="text-xs text-muted-foreground">{settings.enabled ? "Live now across the store" : "Currently off"}</p>
            </div>
          </div>
          <Switch checked={settings.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="sports-master-toggle" />
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Event / League</label>
            <Select value={settings.league_key} onValueChange={setLeague}>
              <SelectTrigger className="mt-1.5" data-testid="sports-league-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {catalog.map((c) => (
                  <SelectItem key={c.key} value={c.key}>
                    {c.emoji} {c.name}{c.live_count > 0 ? `  · ${c.live_count} live` : c.upcoming_count > 0 ? `  · ${c.upcoming_count} upcoming` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Show every N slides</label>
            <Select value={String(settings.every)} onValueChange={(v) => save({ every: Number(v) })}>
              <SelectTrigger className="mt-1.5" data-testid="sports-every-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5, 6].map((n) => <SelectItem key={n} value={String(n)}>After every {n} slide{n > 1 ? "s" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        {saving && <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</p>}
        <Button variant="secondary" size="sm" className="mt-3 w-full sm:w-auto" onClick={previewGoal} disabled={!settings.enabled} data-testid="sports-preview-goal">
          🎉 Preview goal celebration on TVs
        </Button>
      </div>

      {/* Live preview */}
      <div className="bg-card border border-border rounded-xl p-4 md:p-5 mb-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Live preview · {active?.name}</p>
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 live-dot" /> auto-updating
          </span>
        </div>
        {!live || !live.matches?.length ? (
          <p className="text-sm text-muted-foreground py-6 text-center">No matches scheduled right now for {active?.name}. Pick an event with live or upcoming games above.</p>
        ) : (
          <>
          <div className="space-y-2" data-testid="sports-preview">
            {live.matches.slice(0, 6).map((m) => (
              <div key={m.id} className="flex items-center gap-3 bg-secondary/60 rounded-lg px-3 py-2">
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex-shrink-0 ${m.state === "in" ? "bg-red-500 text-white" : "bg-white/10 text-muted-foreground"}`}>
                  {m.state === "in" ? (m.clock || "Live") : m.state === "post" ? "Final" : "Soon"}
                </span>
                <span className="text-sm font-medium truncate flex-1 text-right">{m.away.name}</span>
                <span className="text-base font-black tabular-nums px-2">{m.away.score} – {m.home.score}</span>
                <span className="text-sm font-medium truncate flex-1">{m.home.name}</span>
              </div>
            ))}
          </div>
          {!live.matches.some((m) => m.state === "in" || m.state === "pre") && (
            <p className="text-xs text-amber-400/90 mt-3 text-center" data-testid="sports-finished-note">
              These games have finished — TVs only show live or upcoming matches. Pick another event to broadcast now.
            </p>
          )}
          </>
        )}
      </div>

      {/* Per-TV control */}
      <div className="bg-card border border-border rounded-xl p-4 md:p-5">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Per-TV control</p>
        <p className="text-[11px] text-muted-foreground/70 mb-3">Turn the scoreboard off on specific screens (it stays on everywhere else when broadcasting).</p>
        {tvs.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No TVs paired yet.</p>
        ) : (
          <div className="space-y-2">
            {tvs.map((tv) => (
              <div key={tv.id} className="flex items-center justify-between gap-3 bg-secondary/50 rounded-lg px-3 py-2.5" data-testid={`sports-tv-${tv.id}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <Tv className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{tv.name}</p>
                    {tv.zone && <p className="text-[11px] text-muted-foreground">{tv.zone}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-muted-foreground">{tv.sports_off ? "Off" : "On"}</span>
                  <Switch checked={!tv.sports_off} onCheckedChange={() => toggleTv(tv)} data-testid={`sports-tv-toggle-${tv.id}`} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
